import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  addAnswer,
  addImage,
  addNote,
  addWriteIn,
  clearAnswerImage,
  removeAnswer,
} from "../../redux/survey/actions";
import {
  createSurveyAnswerHandler,
  getStoredAnswer,
  resolveScoreExclude,
} from "../../Survey/helpers/createSurveyAnswerHandler";
import {
  getVisibleSurveyQuestions,
  resolveVisibleQuestionIndex,
} from "../../Survey/helpers/surveyQuestionVisibility";
import {
  filterQuestionsThroughFinish,
  getQuestionIdsAfter,
  omitAnswersByQuestionIds,
} from "../../Survey/helpers/surveyFinishSurvey";
import { getClientConnectQuestionValidationError } from "../../Survey/pages/ClientConnectV2/helpers/clientConnectSurveyQuestions";
import { questionTypes } from "../../Survey/pages/ClientConnectV2/survey/SurveyPreviewBody";
import { normalizeId } from "../../Survey/pages/ClientConnectV2/survey/surveyPreviewUtils";
import {
  setLearningQuestionIndex,
  setLearningQuestionnaireError,
  setLearningQuestionnaireLoading,
  setLearningQuestionnaireSubmitted,
  setLearningQuestionnaireSurvey,
} from "../../redux/learning/actions";
import {
  selectLearningMaxAttempts,
  selectLearningNextSubmitAttempt,
  selectLearningPackageId,
  selectLearningPassMarkPercent,
  selectLearningQuestionIndex,
  selectLearningQuestionnaireError,
  selectLearningQuestionnaireLoading,
  selectLearningQuestionnaireSubmitted,
  selectLearningQuestionnaireSurvey,
  selectLearningUserId,
  selectSurveyAnswers,
} from "../../redux/learning/selectors";
import { fetchLearningSurvey } from "../helpers/fetchLearningSurvey";
import { applyLearningSurveyLocation } from "../helpers/applyLearningSurveyLocation";
import { loadLearningEmployees } from "../helpers/loadLearningEmployees";
import { submitLearningSurvey } from "../helpers/submitLearningSurvey";
import { useLearningQuery } from "./useLearningQuery";

/** L&D knowledge checks treat unset required as required; other Normal rules stay as-is. */
function withLearningRequired(question) {
  if (!question) {
    return question;
  }
  return {
    ...question,
    required: question.required !== false,
  };
}

export function useQuestionnaireFlow({
  course,
  surveyId: surveyIdOverride,
  onQuizComplete,
} = {}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { inviteId: inviteIdFromQuery, locationId: locationIdFromQuery } =
    useLearningQuery();
  const surveyId = surveyIdOverride || course?.surveyId;
  const packageId = useSelector(selectLearningPackageId);
  const userId = useSelector(selectLearningUserId);
  const nextSubmitAttempt = useSelector(selectLearningNextSubmitAttempt);
  const maxAttempts = useSelector(selectLearningMaxAttempts);
  const passMarkPercent = useSelector(selectLearningPassMarkPercent);
  const survey = useSelector(selectLearningQuestionnaireSurvey);
  const loading = useSelector(selectLearningQuestionnaireLoading);
  const error = useSelector(selectLearningQuestionnaireError);
  const questionIndex = useSelector(selectLearningQuestionIndex);
  const answers = useSelector(selectSurveyAnswers);
  const submitted = useSelector(selectLearningQuestionnaireSubmitted);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [surveyErrors, setSurveyErrors] = useState({});
  const [employeesLoading, setEmployeesLoading] = useState(false);
  // Do not seed from localStorage — a prior session's location can be wrong until
  // applyLearningSurveyLocation runs for this survey (esp. when Redux already has it).
  const [hasLocation, setHasLocation] = useState(false);
  const [resolvedLocationId, setResolvedLocationId] = useState(null);
  const preferredQuestionIdRef = useRef(null);
  const finishSurveyInFlightRef = useRef(false);

  const applyResolvedLocation = (nextLocationId) => {
    setResolvedLocationId(nextLocationId);
    setHasLocation(Boolean(nextLocationId));
    if (nextLocationId) {
      // Same render as location change: avoid painting previous location staff.
      setEmployeesLoading(true);
      localStorage.setItem("employees", JSON.stringify([]));
    } else {
      setEmployeesLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadSurvey = async () => {
      if (!surveyId) {
        dispatch(setLearningQuestionnaireError("ld-questionnaire-not-configured"));
        return;
      }

      if (survey?._id === surveyId || survey?.id === surveyId) {
        if (!cancelled) {
          const nextLocationId = applyLearningSurveyLocation(
            survey,
            locationIdFromQuery,
          );
          applyResolvedLocation(nextLocationId);
          // A prior in-flight fetch may have left loading=true; clear it.
          dispatch(setLearningQuestionnaireLoading(false));
        }
        return;
      }

      dispatch(setLearningQuestionnaireLoading(true));

      try {
        const data = await fetchLearningSurvey(surveyId);
        if (cancelled) {
          return;
        }
        const nextLocationId = applyLearningSurveyLocation(
          data,
          locationIdFromQuery,
        );
        applyResolvedLocation(nextLocationId);
        dispatch(setLearningQuestionnaireSurvey(data));
      } catch (loadError) {
        if (!cancelled) {
          dispatch(
            setLearningQuestionnaireError(
              loadError?.message || "ld-unable-load-questionnaire",
            ),
          );
        }
      }
    };

    loadSurvey();

    return () => {
      cancelled = true;
    };
  }, [dispatch, locationIdFromQuery, survey, surveyId]);

  useEffect(() => {
    if (!survey) {
      return;
    }
    const nextLocationId = applyLearningSurveyLocation(
      survey,
      locationIdFromQuery,
    );
    applyResolvedLocation(nextLocationId);
  }, [locationIdFromQuery, survey]);

  useEffect(() => {
    let cancelled = false;

    if (!resolvedLocationId) {
      setEmployeesLoading(false);
      return undefined;
    }

    const locationIdForRequest = resolvedLocationId;

    const loadEmployees = async () => {
      // Location apply already set loading + cleared storage; keep flag true for fetch.
      setEmployeesLoading(true);
      try {
        await loadLearningEmployees(locationIdForRequest);
      } finally {
        if (!cancelled) {
          setEmployeesLoading(false);
        }
      }
    };

    loadEmployees();

    return () => {
      cancelled = true;
    };
  }, [resolvedLocationId, survey?._id, survey?.id]);

  const questions = useMemo(
    () =>
      getVisibleSurveyQuestions(survey?.questions, answers, {
        hasLocation,
      }),
    [survey?.questions, answers, hasLocation],
  );
  const totalQuestions = questions.length;
  const currentQuestion = questions[questionIndex] ?? null;
  const isFirstQuestion = questionIndex === 0;
  const isLastQuestion = totalQuestions > 0 && questionIndex >= totalQuestions - 1;

  useEffect(() => {
    if (currentQuestion?._id) {
      preferredQuestionIdRef.current = currentQuestion._id;
    }
  }, [currentQuestion?._id]);

  useEffect(() => {
    if (!questions.length) {
      return;
    }

    const nextIndex = resolveVisibleQuestionIndex(
      questions,
      preferredQuestionIdRef.current,
      questionIndex,
    );

    if (nextIndex !== questionIndex) {
      dispatch(setLearningQuestionIndex(nextIndex));
    }
  }, [dispatch, questions, questionIndex]);

  const questionById = useMemo(() => {
    const map = new Map();
    (survey?.questions || []).forEach((question) => {
      map.set(normalizeId(question._id), question);
    });
    return map;
  }, [survey?.questions]);

  const setAnswer = createSurveyAnswerHandler({
    answers,
    questionById,
    addAnswer: (payload) => dispatch(addAnswer(payload)),
    addWriteIn: (payload) => dispatch(addWriteIn(payload)),
    addNote: (payload) => dispatch(addNote(payload)),
    addImage: (payload) => dispatch(addImage(payload)),
    clearAnswerImage: (payload) => dispatch(clearAnswerImage(payload)),
    onValidationChange: setSurveyErrors,
    getValidationError: (question, projectedAnswer) =>
      getClientConnectQuestionValidationError(
        withLearningRequired(question),
        projectedAnswer,
        intl,
      ),
  });

  const currentAnswer = currentQuestion
    ? getStoredAnswer(answers, currentQuestion._id)
    : undefined;

  const currentValidationError = currentQuestion
    ? getClientConnectQuestionValidationError(
        withLearningRequired(currentQuestion),
        currentAnswer,
        intl,
      )
    : null;

  const currentAnswered = Boolean(currentQuestion) && !currentValidationError;

  const hasAnswerValue = (() => {
    const value = currentAnswer?.value;
    if (value == null || value === "") {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "object") {
      return Object.keys(value).length > 0;
    }
    return true;
  })();

  const currentFieldError = currentQuestion
    ? surveyErrors[normalizeId(currentQuestion._id)] ??
      surveyErrors[currentQuestion._id] ??
      (hasAnswerValue && currentValidationError ? currentValidationError : null)
    : null;

  const validateQuestion = (question) => {
    if (!question) {
      return null;
    }
    const answer = getStoredAnswer(answers, question._id);
    return getClientConnectQuestionValidationError(
      withLearningRequired(question),
      answer,
      intl,
    );
  };

  const validateVisibleQuestions = () => {
    const nextErrors = {};
    let firstInvalidIndex = -1;

    questions.forEach((question, index) => {
      const errorResult = validateQuestion(question);
      if (errorResult) {
        nextErrors[normalizeId(question._id)] = errorResult;
        if (firstInvalidIndex === -1) {
          firstInvalidIndex = index;
        }
      }
    });

    setSurveyErrors(nextErrors);
    return { hasErrors: Object.keys(nextErrors).length > 0, firstInvalidIndex };
  };

  const allAnswered = useMemo(
    () =>
      questions.every(
        (question) =>
          !getClientConnectQuestionValidationError(
            withLearningRequired(question),
            getStoredAnswer(answers, question._id),
            intl,
          ),
      ),
    [questions, answers, intl],
  );

  const sidebarSteps = useMemo(() => {
    const progressSteps = course?.progressSteps ?? [];
    const questionSubtitle = totalQuestions
      ? `${totalQuestions} question${totalQuestions === 1 ? "" : "s"}`
      : progressSteps.find((step) => step.id === "questionnaire")?.subtitle;

    return progressSteps.map((step) => {
      if (step.id === "confirmation") {
        return { ...step, status: "pending" };
      }

      if (step.id === "questionnaire") {
        return {
          ...step,
          locked: false,
          subtitle: questionSubtitle,
          status: "active",
          contentComplete: submitted,
        };
      }

      if (step.locked) {
        return { ...step, locked: false, status: "completed" };
      }

      return { ...step, status: "completed" };
    });
  }, [course?.progressSteps, totalQuestions, submitted]);

  const goToNextQuestion = async () => {
    if (submitting || !currentQuestion) {
      return;
    }

    const currentError = validateQuestion(currentQuestion);
    if (currentError) {
      setSurveyErrors((prev) => ({
        ...prev,
        [normalizeId(currentQuestion._id)]: currentError,
      }));
      return;
    }

    if (isLastQuestion) {
      const { hasErrors, firstInvalidIndex } = validateVisibleQuestions();
      if (hasErrors) {
        if (firstInvalidIndex >= 0) {
          preferredQuestionIdRef.current = questions[firstInvalidIndex]._id;
          dispatch(setLearningQuestionIndex(firstInvalidIndex));
        }
        return;
      }

      setSubmitting(true);
      setSubmitError(null);

      try {
        const submittedOk = await runLearningSubmit(answers, questions);
        if (!submittedOk) {
          setSubmitError("ld-unable-submit-answers");
        }
      } catch {
        setSubmitError("ld-unable-submit-answers");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    dispatch(
      setLearningQuestionIndex(Math.min(questionIndex + 1, totalQuestions - 1)),
    );
  };

  const runLearningSubmit = async (answersForSubmit, questionsForSubmit) => {
    const inviteId =
      inviteIdFromQuery || localStorage.getItem("invite_id") || "";

    const submitResult = await submitLearningSurvey({
      answers: answersForSubmit,
      questions: questionsForSubmit,
      surveyId,
      inviteId,
      userId,
      packageId,
      attemptNumber: nextSubmitAttempt,
      questionCount: questionsForSubmit.length,
    });

    if (!submitResult.success) {
      return false;
    }

    dispatch(setLearningQuestionnaireSubmitted(true));
    onQuizComplete?.(submitResult);
    return true;
  };

  /** Option-level finish_survey: clear later answers and submit (Normal parity). */
  const finishSurveyEarly = async (question, selectedValue) => {
    if (
      finishSurveyInFlightRef.current ||
      submitting ||
      submitted ||
      !question
    ) {
      return;
    }
    finishSurveyInFlightRef.current = true;

    const answerKey = normalizeId(question._id);
    const projectedAnswer = {
      ...getStoredAnswer(answers, question._id),
      value: selectedValue,
      score_exclude: resolveScoreExclude(question, selectedValue),
    };
    const currentError = getClientConnectQuestionValidationError(
      withLearningRequired(question),
      projectedAnswer,
      intl,
    );
    if (currentError) {
      finishSurveyInFlightRef.current = false;
      setSurveyErrors((prev) => ({
        ...prev,
        [answerKey]: currentError,
      }));
      return;
    }

    const allQuestions = survey?.questions || [];
    const laterIds = getQuestionIdsAfter(allQuestions, question._id);
    // Keep later answers when computing hide/route visibility so questions they
    // hide do not reappear. Strip them only from the submit payload.
    const answersWithFinish = {
      ...answers,
      [answerKey]: projectedAnswer,
    };
    const answersForSubmit = omitAnswersByQuestionIds(
      answersWithFinish,
      laterIds,
    );

    const visibleThroughFinish = filterQuestionsThroughFinish(
      allQuestions,
      getVisibleSurveyQuestions(allQuestions, answersWithFinish, {
        hasLocation,
      }),
      question._id,
    );

    // Ensure prior visible questions (before finish) are still valid.
    let firstInvalid = null;
    const nextErrors = {};
    visibleThroughFinish.forEach((visibleQuestion) => {
      const answer =
        normalizeId(visibleQuestion._id) === answerKey
          ? projectedAnswer
          : getStoredAnswer(answersForSubmit, visibleQuestion._id);
      const errorResult = getClientConnectQuestionValidationError(
        withLearningRequired(visibleQuestion),
        answer,
        intl,
      );
      if (errorResult) {
        nextErrors[normalizeId(visibleQuestion._id)] = errorResult;
        if (!firstInvalid) {
          firstInvalid = visibleQuestion;
        }
      }
    });

    if (Object.keys(nextErrors).length) {
      finishSurveyInFlightRef.current = false;
      setSurveyErrors(nextErrors);
      if (firstInvalid) {
        preferredQuestionIdRef.current = firstInvalid._id;
        const jumpIndex = questions.findIndex(
          (item) => normalizeId(item._id) === normalizeId(firstInvalid._id),
        );
        if (jumpIndex >= 0) {
          dispatch(setLearningQuestionIndex(jumpIndex));
        }
      }
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const submittedOk = await runLearningSubmit(
        answersForSubmit,
        visibleThroughFinish,
      );
      if (!submittedOk) {
        setSubmitError("ld-unable-submit-answers");
      } else {
        laterIds.forEach((id) => {
          dispatch(removeAnswer({ id: normalizeId(id) }));
        });
      }
    } catch {
      setSubmitError("ld-unable-submit-answers");
    } finally {
      setSubmitting(false);
      // Always clear so retryQuiz / a later attempt can early-finish again.
      finishSurveyInFlightRef.current = false;
    }
  };

  const goToPreviousQuestion = () => {
    dispatch(setLearningQuestionIndex(Math.max(0, questionIndex - 1)));
  };

  return {
    survey,
    surveyId,
    loading,
    error,
    submitError,
    questions,
    questionIndex,
    currentQuestion,
    totalQuestions,
    isFirstQuestion,
    isLastQuestion,
    answers,
    setAnswer,
    finishSurveyEarly,
    setQuestionError: (questionId, error) => {
      const key = normalizeId(questionId);
      setSurveyErrors((prev) => {
        if (!error) {
          if (!prev[key] && !prev[questionId]) {
            return prev;
          }
          const next = { ...prev };
          delete next[key];
          delete next[questionId];
          return next;
        }
        return { ...prev, [key]: error };
      });
    },
    currentAnswered,
    allAnswered,
    employeesLoading,
    surveyErrors: currentQuestion
      ? {
          ...surveyErrors,
          ...(currentFieldError
            ? { [normalizeId(currentQuestion._id)]: currentFieldError }
            : {}),
        }
      : surveyErrors,
    submitted,
    submitting,
    goToNextQuestion,
    goToPreviousQuestion,
    sidebarSteps,
    passMarkPercent,
    nextSubmitAttempt,
    maxAttempts,
  };
}

export { questionTypes };
